import {
  Int32MaxValue,
  QuantityInputMaxLength,
  QuantityInputMaxLengthForThirdPartyBrands,
} from '../../../../utils/constants';
import React, { useState } from 'react';
import { formatCharge, formatQuantity } from '../../../../utils/formatter';
import { getFullPackageQuantity, isInternalBrand } from '../../../../main/selectors';
import { getMinimumQuantity, getRawPriceTable } from '../../../../price/selectors';

import FeedbackWrapper from '../../../common/FeedbackWrapper';
import PropTypes from 'prop-types';
import SpinnerWrapper from '../../../common/SpinnerWrapper';
import styles from './CustomQuantity.scss';
import useOrderSummaryDetails from '../../../../ordersummary/hooks/useOrderSummaryDetails';
import { useSelector } from 'react-redux';
import { validateLockedQuantity } from '../../../../utils/quantityValidation';

const CustomQuantity = ({ quantity, onAcceptQuantity, onChange, onFocus, ...rest }) => {
  const [focused, setFocused] = useState(false);
  const [validation, setValidation] = useState({
    isValid: true,
    message: '',
  });

  const internalBrand = useSelector(isInternalBrand);
  const maxLength = internalBrand ? QuantityInputMaxLength : QuantityInputMaxLengthForThirdPartyBrands;

  const fullPackageQty = useSelector(getFullPackageQuantity);
  const miniQty = useSelector(getMinimumQuantity);
  const priceTable = useSelector(getRawPriceTable);

  const details = useOrderSummaryDetails();
  const { chargeDetails, validation: finalValidation } = details;
  const { essentialCharge } = chargeDetails;
  const { hasDiscount, isCalculating, unitPrice } = essentialCharge;

  const filterInt = (val) => {
    if (/^[-+]?(\d+|Infinity)$/.test(val)) {
      return Number(val);
    } else {
      return NaN;
    }
  };

  const handleAcceptQuantity = () => {
    const validation = validateLockedQuantity(quantity, fullPackageQty, miniQty, priceTable, internalBrand);
    setValidation(validation);
    if (validation.isValid && !!onAcceptQuantity && typeof onAcceptQuantity === 'function') {
      onAcceptQuantity();
    }
  };

  const handleBlur = () => {
    setFocused(false);
    handleAcceptQuantity();
  };

  const handleChange = (e) => {
    setValidation({
      isValid: true,
      message: '',
    });

    let qty;
    if (e.target.validity.valid) {
      let qty_str = e.target.value.trim();
      qty = qty_str === '' ? 0 : filterInt(qty_str);
      if (isNaN(qty) || qty === Infinity || qty < 0 || qty > Int32MaxValue) {
        qty = quantity;
      }
    } else {
      qty = quantity;
    }

    if (!!onChange && typeof onChange === 'function') {
      onChange(qty);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    onFocus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
      handleAcceptQuantity();
    } else if (e.key === 'Escape' || e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    e.target.focus();
  };

  const handleTouchEnd = (e) => {
    e.stopPropagation();
    e.target.focus();
  };

  return (
    <div className={styles['custom-quantity']}>
      <label id="custom-quantity-input-label">Custom quantity</label>
      <div className={styles['quantity-holder']}>
        <input
          {...rest}
          aria-describedby="custom-quantity-input-description"
          aria-labelledby="custom-quantity-input-label"
          autoComplete="off"
          autoCorrect="off"
          className={`${styles['quantity-input']}${!validation.isValid ? ` ${styles['invalid']}` : ''} form-control`}
          id="custom-quantity-input"
          maxLength={maxLength}
          pattern="[0-9]*"
          placeholder={focused ? '' : '0'}
          spellCheck={false}
          type="text"
          value={quantity === 0 ? '' : quantity}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          onTouchEnd={handleTouchEnd}
        />
        {!validation.isValid && <FeedbackWrapper tooltip>{validation.message}</FeedbackWrapper>}
        {finalValidation.isValid && !isCalculating ? (
          <small id="custom-quantity-input-description" className={styles['quantity-description']}>
            <span className={`${styles['unit-price']}${hasDiscount ? ` ${styles['sale']}` : ''}`}>
              {`${formatCharge(unitPrice)}`}
            </span>
            &nbsp;
            {`/ unit. Minimum Qty: ${formatQuantity(miniQty)}`}
          </small>
        ) : (
          <>
            {isCalculating && <SpinnerWrapper meaning="Calculating Unit Price" />}
            <small id="custom-quantity-input-description" className={styles['quantity-description']}>
              {`Minimum Qty: ${formatQuantity(miniQty)}`}
            </small>
          </>
        )}
      </div>
    </div>
  );
};

CustomQuantity.propTypes = {
  quantity: PropTypes.number.isRequired,
  onAcceptQuantity: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default CustomQuantity;
